import React, { useEffect, useState } from "react";
import useAuth from '../../../hooks/useAuth';
import Select from 'react-select';
import GearSpinner from '../../../components/GearSpinner/GearSpinner';
import Loader from '../../../components/Loader/Loader';
import logo from '../../../assets/images/big-logo.jpg';
import printIcon from '../../../assets/images/print-img.png';
import shareIcon from '../../../assets/images/share-img.png';
import pdfIcon from '../../../assets/images/pdf-img.png';
import saveIcon from '../../../assets/images/savedraft-icon.png';
import infoIcon from '../../../assets/images/info-icon.png';
import QuoteMeService from "../../../services/QuoteMeService";
import PreQuoteSalesTable from "../PreQuoteSalesTable";
import AlertMessage from "../../../components/AlertMessage/AlertMessage";
import ShareQuoteMeModal from '../../../components/Modals/ShareQuoteMeModal';
import moment from "moment-timezone";
import { formatUSD } from '../../../utils/common';
import { Validation } from "../../../utils/validation";


const PreQuoteTab = ({ active }) => {
    const {user} = useAuth();
    const [showSpinner, setShowSpinner] = React.useState(false);
    const [showLoader, setShowLoader] = React.useState(true);
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [products,setProducts] = useState([]);
    const [productMap, setProductMap] = useState({});
    const [multiplierMap, setMultiplierMap] = useState({});
    const [multiplierOptions, setMultiplierOptions] = useState([]);
    const [isProducts, setIsProducts] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [estTime, setEstTime] = useState("");
    const [showShareModal, setShowShareModal] = useState(false);
    const [rowErrors, setRowErrors] = useState({});

    const [formData, setFormData] = React.useState({
        order_number: '',
        order_date: moment().format('YYYY-MM-DD'),
        billing_address: null,
        shipping_address: null,
        notes: null,
        sub_total:"0.00",
        sales_tax:"0.00",
        total:"0.00",
        sales_data:[{
            "type":"Sale",
            "product_number":"",
            "description":"",
            "unit_price":"0.00",
            "quantity":"",
            "total_price":"0.00",
            "multiplier":null

        }]
    });
    const quoteMeService = new QuoteMeService();

    const getNextQuoteOrderNumber = () => {
        quoteMeService.getNextQuoteOrderNumber().then(response => {
            setShowLoader(false);
            const data = response.data;
            if(data.status === 'success'){
                setFormData(prev => ({
                    ...prev,
                    order_number: data.result.order_number
                }))
            }
        }).catch(error => {
            console.error('Error fetching next quote order number:', error);
        });
    }


   
    useEffect(() => {
        setEstTime(
            moment()
            .tz("America/New_York")
            .format("MMMM DD, YYYY hh:mm:ss A z")
        );
    }, []);

    useEffect(() => {
      if(user && active){
        getNextQuoteOrderNumber();
      }
    }, [user, active]);

    useEffect(()=> {
      if(products.length === 0) {
        quoteMeService.getCustomerMultipliers({
          "portal_user_name":user.name
        }).then(response => {
          const data = response.data;
          if (data.status === "success") {
            // 1️⃣ Build multiplier map
            const mMap = {};
            const mOptionsMap = {}; // key-based dedupe

            data.result.customers.forEach(customer => {
              customer.multipliers.forEach(m => {
                const val = Number(m.customer_multiplier);

                if (!Number.isFinite(val)) return;

                if (!mOptionsMap[val]) {
                  if(val > 0) {
                    mOptionsMap[val] = {
                      value: val.toFixed(3),
                      label: val.toFixed(3)
                    };
                  }
                }

                // map value → numeric multiplier
                mMap[val] = val;
              });
            });

            const sortedMultiplierOptions = Object.values(mOptionsMap).sort(
              (a, b) => Number(a.value) - Number(b.value)
            );

            setMultiplierMap(mMap);
            setMultiplierOptions(Object.values(sortedMultiplierOptions));


            // 2️⃣ Build product map (num → product)
            const rawProducts = Array.isArray(data.result.products)
                ? data.result.products
                : Object.values(data.result.products).flat();

            const pMap = {};
            const productOptions = [];

            rawProducts.forEach(p => {
                pMap[p.num] = p;
                productOptions.push({
                    value: p.num,
                    label: p.num
                });
            });

            if(rawProducts.length > 0) {
                setIsProducts(true);
            }

            setProductMap(pMap);
            setProducts(productOptions);

            setFormData(prev => ({
                ...prev,
                portal_user_id: user.id
            }));

          }
        }).catch(error => {
          console.error('Error fetching customer multipliers:', error);
        });
      }
    });

    if (!active) return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]:value
        }));
    }


    const handleSameAsBilling = (e) => {
        const checked = e.target.checked;
        setSameAsBilling(checked);

        if (checked) {
            setFormData(prev => ({
                ...prev,
                shipping_address: prev.billing_address
            }));
        } else {

            setFormData(prev => ({
                ...prev,
                shipping_address: null
            }));
        }
    };

    const validateSalesRows = () => {
        let currentErrors = {};
        formData.sales_data.forEach((row, index) => {
            const errors = Validation(row, ['product_number', 'quantity' ,'multiplier']);
            
            if (errors && Object.keys(errors).length > 0) {
                currentErrors[index] = errors;
            }
        });
        setRowErrors(currentErrors);
        return Object.keys(currentErrors).length === 0;
    };

    const handleShareClick = async () => {
        setShowShareModal(true);
    };

    const saveQuote = async ({ resetAfterSave = true } = {}) => {
        const est_time = moment()
            .tz("America/New_York")
            .format("MMMM DD, YYYY hh:mm:ss A z");

        const payload = {
            ...formData,
            est_timestamp: est_time
        };

        try {
          const response = await quoteMeService.saveQuoteData(payload);
          const data = response.data;
          
          if (data.status !== 'success') {
              setAlert({ type: 'error', message: data.message });
              throw new Error(data.message);
          }

          setAlert({ type: 'success', message: data.message });

          if (resetAfterSave) {
            setFormData(prev => ({
                ...prev,
                order_number: '',
                order_date: moment().format('YYYY-MM-DD'),
                billing_address: null,
                shipping_address: null,
                notes: null,
                sub_total:"0.00",
                sales_tax:"0.00",
                total:"0.00",
                sales_data:[{
                    "type":"Sale",
                    "product_number":"",
                    "description":"",
                    "unit_price":"0.00",
                    "quantity":"",
                    "total_price":"0.00",
                    "multiplier":null

                }]
            }));

            setSameAsBilling(false);   // reset checkbox
            getNextQuoteOrderNumber(); // fetch new quote #
          }

          return payload;
        } catch (error) {
            setAlert({ type: 'error', message: error.message || 'Save failed' });
            throw error;
        } finally {
            setShowLoader(false);
        }
    }

    const handleSubmit = async () => {
      if (!validateSalesRows()) return;
      setShowLoader(true);
      await saveQuote({ resetAfterSave: true });
    };

    const normalizeQuoteForShare = (payload) => ({
      ...payload,
      order_date: moment(payload.order_date).format('MM/DD/YYYY'),
      date_schedule: moment(payload.date_schedule).format('MM/DD/YYYY'),
      sub_total: formatUSD(payload.sub_total || 0),
      sales_tax: formatUSD(payload.sales_tax || 0),
      total: formatUSD(payload.total || 0),
      sales_data: (payload.sales_data || []).map(item => ({
        ...item,
        unit_price: formatUSD(item.unit_price || 0),
        total_price: formatUSD(item.total_price || 0)
      }))
    });

    const handleShare = async (email, quote_note) => {
        // 1️⃣ Save quote (NO reset yet)
        if (!validateSalesRows()) {
            throw new Error("Please fix validation errors before sharing.");
        }
        const savedPayload = await saveQuote({ resetAfterSave: true });
        const sharePayload = ({
            ...savedPayload,
            email_to: [email],
            quote_note : quote_note,
            format: 'both'
        });
        
        // 2️⃣ Share quote
        const payload = normalizeQuoteForShare(sharePayload);
        
        const response = await quoteMeService.shareQuote(payload);
        

        if (response.data.response !== 'success') {
            throw new Error(response.data.message || 'Share failed');
        }

        return true; // modal uses this to show success
    };

    const exportPdf = () => {
        setShowLoader(true);

        quoteMeService.exportQuoteOrderPDF(formData)
            .then(response => {
                setShowLoader(false);

                // response.data IS already a Blob
                const blob = new Blob([response.data], {
                    type: 'application/pdf'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = `quote-${formData.order_number}.pdf`;

                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(error => {
                setShowLoader(false);
                console.error('Error exporting PDF:', error);
            });
    };
    
    return (
        <React.Fragment>
          <div className="container tab-pane active">
            <div className="row">
              <AlertMessage
                  type={alert.type}
                  message={alert.message}
                  onClose={() => setAlert({ type: '', message: '' })}
              />
              <div className="col-md-10">

                {/* Header */}
                <div className="section-div1">
                  <div className="row">
                    <div className="col-md-8">
                      <img src={logo} alt="Techtop" />
                      <h3>Techtop Industries, Inc.</h3>
                      <p className="m-0 text-20">Techtop Industries, Inc.</p>
                      <p className="m-0 text-20">2815 Colonnades Court</p>
                      <p className="m-0 text-20">Peachtree Corners, GA 30071</p>
                    </div>

                    <div className="col-md-4">
                      <h3 className="text-center">Quote</h3>
                      <table className="table table-bordered quote-table text-center">
                        <thead>
                          <tr>
                            <th>Order #</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>{formData.order_number}</td>
                            <td>{moment().format('MM/DD/YYYY')}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Billing / Shipping */}
                <div className="row">
                  <div className="col-md-6">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>Bill To:</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <textarea className="form-control" id="billing_address" name="billing_address" rows="2" placeholder="Billing Address" value={formData.billing_address || ''} onChange={handleInputChange}></textarea>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="col-md-6">
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th style={{ borderRight: 0 }}>Ship To: US</th>
                          <th style={{ borderLeft: 0 }}>
                            <div className="form-check techtop-div-check">
                              <input className="form-check-input" type="checkbox" name="same_billing" id="same_billing" checked={sameAsBilling} onChange={handleSameAsBilling}/>
                              <label className="form-check-label" htmlFor="same_billing">Same</label>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td colSpan="2">
                            <textarea className="form-control" id="shipping_address" rows="2" placeholder="Entry" name="shipping_address" value={formData.shipping_address || ''} onChange={handleInputChange}></textarea>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Items Table */}
                <div className="table-responsive">
                  <PreQuoteSalesTable
                    salesData={formData.sales_data || []}
                    setFormData={setFormData}
                    products={products}
                    productMap={productMap}
                    multiplierMap={multiplierMap}
                    multiplierOptions={multiplierOptions} 
                    rowErrors={rowErrors}
                  />
                </div>

                {/* Summary */}
                <div className="table-responsive mt-5">
                  <table className="table table-bordered techtop-quote-table2">
                    <tbody>
                      <tr>
                        <td rowSpan="3">
                          <label>Notes:</label>
                          <textarea className="form-control" id="notes" rows="2" placeholder="Notes" name="notes" value={formData.notes || ''} onChange={handleInputChange}></textarea>
                        </td>
                        <td className="text-end">
                          <strong>Subtotal:</strong>
                        </td>
                        <td className="text-end">
                          <strong>${formatUSD(formData.sub_total)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-end">
                          <strong>Sales Tax:</strong>
                        </td>
                        <td className="text-end">
                          <strong>${formatUSD(formData.sales_tax)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-end">
                          <strong>Total:</strong>
                        </td>
                        <td className="text-end">
                          <strong>${formatUSD(formData.total)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="dark-color1">
                  {estTime}
                </p>
              </div>

              {/* Sidebar */}
              <div className="col-md-2">
                <ul className="net-pricing-report-list">
                    <li><button className="btn-link-style" onClick={() => handleSubmit()}><img src={saveIcon} alt="Save" /> Save</button></li> 
                    {/* <li><button className="btn-link-style"><img src={printIcon} alt="Print" /> Print</button></li> */}
                    <li><button className="btn-link-style" onClick={() => handleShareClick()}><img src={shareIcon} alt="Share" /> Share</button></li> 
                    {/* <li><button className="btn-link-style" onClick={() => exportPdf()}><img src={pdfIcon} alt="PDF" />Export PDF</button></li>  */}
                </ul>
              </div>
            </div>
            {showSpinner && <GearSpinner />}
            <Loader isLoading={showLoader} message="Processing..."/>
            <ShareQuoteMeModal 
              isShow={showShareModal} 
              payload={formData} 
              onClose={() => setShowShareModal(false)} 
              onShare={handleShare}
            />
          </div>
        </React.Fragment>
    )

}
export default PreQuoteTab;